import type { JobType } from '@/services/api/domains/uploads.api'

/**
 * The file pieces ImportModal and the replace flow (#1547) share: what kind of file this is, which job
 * publishes it, the table name a file name sanitises to, and where a CSV keeps its coordinates.
 */

/** A file the publish path understands, detected from its name. */
export type ImportFileType = 'csv' | 'shapefile' | 'geopackage' | 'geojson' | 'gdb'

/** `.gdb.zip` is checked BEFORE a bare `.zip`, so a FileGDB archive is never taken for a shapefile zip. */
export function detectFileType(fileName: string): ImportFileType | null {
  const lower = fileName.toLowerCase()
  if (lower.endsWith('.gdb.zip')) return 'gdb'
  const ext = lower.split('.').pop()
  if (ext === 'csv') return 'csv'
  if (ext === 'zip') return 'shapefile'
  if (ext === 'gpkg') return 'geopackage'
  if (ext === 'geojson' || ext === 'json') return 'geojson'

  return null
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export function sanitizeTableName(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 63) || 'layer'
  )
}

/** The file name without its extension(s): `roads.gdb.zip` → `roads`. */
export function fileBaseName(fileName: string): string {
  return fileName.replace(/(\.[^/.]+)+$/, '')
}

export const FILE_TYPE_TO_JOB_TYPE: Record<ImportFileType, JobType> = {
  csv: 'publish_csv',
  geojson: 'publish_geojson',
  shapefile: 'publish_shapefile',
  geopackage: 'publish_geopackage',
  gdb: 'publish_gdb',
}

/** Header names (lowercased) that hold a CSV's latitude / longitude. Shared with CsvColumnMapper. */
export const CSV_LAT_PATTERNS: readonly string[] = ['latitude', 'lat', 'y', 'lat_dd', 'latdd']
export const CSV_LON_PATTERNS: readonly string[] = ['longitude', 'lon', 'lng', 'long', 'x', 'lon_dd', 'londd']

/**
 * The columns a CSV most likely keeps its coordinates in, as WRITTEN in the header (the column mapper's
 * options are the written names), parsed the way CsvColumnMapper parses: first non-blank line,
 * quotes stripped.
 */
export function detectCsvLatLon(content: string, delimiter = ','): { lat: string | null; lon: string | null } {
  const headerLine = content.split(/\r?\n/).find(line => line.trim()) ?? ''
  const headers = headerLine.split(delimiter).map(h => h.trim().replace(/^["']|["']$/g, ''))

  return {
    lat: headers.find(h => CSV_LAT_PATTERNS.includes(h.toLowerCase())) ?? null,
    lon: headers.find(h => CSV_LON_PATTERNS.includes(h.toLowerCase())) ?? null,
  }
}
